import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { fetchInstitutionDonorAchievements } from '@/lib/institution-donors'
import { ACHIEVEMENT_DEFINITIONS } from '@/lib/landing-achievement-definitions'
import type {
  InstitutionDonorAchievement,
  InstitutionDonorAchievementsResponse,
} from '@/types/institution-donor'
import styles from './DonorAchievementsSection.module.css'

type Props = { donorId: number; token?: string | null }

export function DonorAchievementsSection({ donorId, token }: Props) {
  const [data, setData] = useState<InstitutionDonorAchievementsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const request = useRef<AbortController | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    request.current = controller
    setData(null)
    setLoading(true)
    setError(false)
    void fetchInstitutionDonorAchievements(donorId, 1, token, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setData(result)
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => {
      controller.abort()
      request.current?.abort()
    }
  }, [donorId, token])

  const loadMore = async () => {
    request.current?.abort()
    const controller = new AbortController()
    request.current = controller
    setLoading(true)
    setError(false)
    try {
      const result = await fetchInstitutionDonorAchievements(
        donorId,
        (data?.pagination.current_page ?? 0) + 1,
        token,
        controller.signal
      )
      if (!controller.signal.aborted)
        setData((previous) => ({
          ...result,
          campaigns: [
            ...new Map(
              [...(previous?.campaigns ?? []), ...result.campaigns].map((campaign) => [
                campaign.id,
                campaign,
              ])
            ).values(),
          ],
        }))
    } catch {
      if (!controller.signal.aborted) setError(true)
    } finally {
      if (!controller.signal.aborted) setLoading(false)
    }
  }

  return (
    <section className={styles.section} aria-labelledby="donor-achievements-title">
      <header className={styles.heading}>
        <span className={styles.headingIcon}>
          <Icon icon="mdi:medal-outline" aria-hidden />
        </span>
        <div>
          <span className={styles.eyebrow}>Jornada do cliente</span>
          <h2 id="donor-achievements-title">Selos conquistados</h2>
          <p>Conquistas atuais e a data em que os critérios foram atingidos.</p>
        </div>
      </header>
      {(data?.profile_achievement || data?.avatar_achievement) && (
        <div className={styles.profile}>
          <h3>
            Conta Sortee <small>Válido em todas as campanhas da plataforma</small>
          </h3>
          {data.profile_achievement && <Badge achievement={data.profile_achievement} />}
          {data.avatar_achievement && <Badge achievement={data.avatar_achievement} />}
        </div>
      )}
      <div className={styles.campaigns}>
        {data?.campaigns.map((campaign, index) => (
          <details key={campaign.id} className={styles.campaign} open={index === 0}>
            <summary>
              <Icon icon="mdi:chevron-right" className={styles.chevron} aria-hidden />
              <strong>{campaign.title ?? `Campanha #${campaign.id}`}</strong>
              <span>
                {campaign.achievements.length}{' '}
                {campaign.achievements.length === 1 ? 'selo' : 'selos'}
              </span>
            </summary>
            <div className={styles.badges}>
              {campaign.achievements.map((badge) => (
                <Badge key={badge.id} achievement={badge} />
              ))}
            </div>
          </details>
        ))}
      </div>
      {!loading &&
        !error &&
        !data?.profile_achievement &&
        !data?.avatar_achievement &&
        !data?.campaigns.length && (
          <p className={styles.empty}>Este cliente ainda não conquistou selos.</p>
        )}
      {loading && (
        <p role="status" className={styles.feedback}>
          Carregando selos…
        </p>
      )}
      {error && (
        <p role="alert" className={styles.error}>
          Não foi possível carregar os selos. Tente novamente.
        </p>
      )}
      {(error || (data && data.pagination.current_page < data.pagination.last_page)) && (
        <button
          type="button"
          className={styles.more}
          onClick={() => void loadMore()}
          disabled={loading}
        >
          {error
            ? 'Tentar novamente'
            : `Ver mais campanhas (${data?.campaigns.length} de ${data?.pagination.total})`}
        </button>
      )}
    </section>
  )
}

function Badge({ achievement }: { achievement: InstitutionDonorAchievement }) {
  const definition = ACHIEVEMENT_DEFINITIONS.find((item) => item.id === achievement.id)
  const date = achievement.unlocked_at ? new Date(achievement.unlocked_at) : null
  const hasDate = date !== null && Number.isFinite(date.getTime())
  return (
    <article className={styles.badge}>
      {definition && <img src={definition.image} alt="" loading="lazy" />}
      <div className={styles.badgeInfo}>
        <span className={styles.badgeNumber}>Selo {definition?.number ?? ''}</span>
        <h4>{definition?.title ?? achievement.title}</h4>
        {achievement.title !== definition?.title && (
          <p className={styles.customTitle}>{achievement.title}</p>
        )}
        <span className={styles.dateLabel}>
          {hasDate ? 'Conquistado em' : 'Data não registrada'}
        </span>
        {hasDate ? (
          <time dateTime={achievement.unlocked_at!}>
            {date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' })}
          </time>
        ) : (
          <small className={styles.legacy}>
            Conquista reconhecida, sem data histórica disponível.
          </small>
        )}
      </div>
    </article>
  )
}
